/**
 * Singleton audio player.
 *
 * Plays single files (`play(id, filename)`) or a sequential queue
 * (`playQueue(queueId, items, gap)`). The queue mode is used by section
 * "play all" buttons — items advance back-to-back with a small gap, and
 * `state.queueId` is set so UI can show a queue-running indicator.
 *
 * Any external `play()` or `stop()` call cancels the current queue. A
 * generation counter makes interruption clean across async boundaries.
 *
 * Failures (e.g., 404) are silent except for a console warning.
 */

const AUDIO_BASE = '/audio';

type Listener = (state: PlayerState) => void;

export type PlayerState = {
  playingId: string | null; // logical id of the currently playing item
  playingFile: string | null; // mp3 filename
  status: 'idle' | 'loading' | 'playing' | 'error';
  /** Identity of the active queue, if any. Null when no queue is running. */
  queueId: string | null;
};

export type QueueItem = {
  id: string;
  filename: string;
};

class AudioPlayer {
  private el: HTMLAudioElement | null = null;
  private state: PlayerState = {
    playingId: null,
    playingFile: null,
    status: 'idle',
    queueId: null,
  };
  private listeners = new Set<Listener>();
  private generation = 0;

  // Queue state — only meaningful when state.queueId !== null.
  private queue: QueueItem[] = [];
  private queueIndex = 0;
  private queueGap = 250;
  private queueTimer: ReturnType<typeof setTimeout> | null = null;

  private setState(patch: Partial<PlayerState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): PlayerState {
    return this.state;
  }

  stop() {
    this.generation++;
    this.clearQueueTimer();
    this.queue = [];
    this.queueIndex = 0;
    if (this.el) {
      this.el.pause();
      this.el.currentTime = 0;
    }
    this.setState({
      playingId: null,
      playingFile: null,
      status: 'idle',
      queueId: null,
    });
  }

  /**
   * External single-item play. Toggles off if the same id is already playing.
   * Always cancels any active queue.
   */
  async play(id: string, filename: string): Promise<void> {
    // Toggle behaviour: tapping the currently-playing item stops it.
    if (
      this.state.playingId === id &&
      this.state.queueId === null &&
      this.state.status !== 'idle'
    ) {
      this.stop();
      return;
    }
    // Any user-initiated single play discards a running queue.
    this.clearQueueTimer();
    this.queue = [];
    this.queueIndex = 0;
    if (this.state.queueId !== null) this.setState({ queueId: null });
    await this.playOne(id, filename);
  }

  /**
   * Play `items` back-to-back, with `gap` ms between each. `queueId` lets
   * the UI tell which queue is active. Re-calling with the same `queueId`
   * while it's already running stops the queue (toggle).
   */
  async playQueue(queueId: string, items: QueueItem[], gap = 250): Promise<void> {
    if (this.state.queueId === queueId) {
      this.stop();
      return;
    }
    this.stop(); // clears any prior queue + audio
    if (items.length === 0) return;
    this.queue = items;
    this.queueIndex = 0;
    this.queueGap = gap;
    this.setState({ queueId });
    await this.advanceQueue();
  }

  private async advanceQueue() {
    if (this.queueIndex >= this.queue.length) {
      // Queue finished naturally.
      this.queue = [];
      this.queueIndex = 0;
      this.setState({
        playingId: null,
        playingFile: null,
        status: 'idle',
        queueId: null,
      });
      return;
    }
    const item = this.queue[this.queueIndex++]!;
    await this.playOne(item.id, item.filename, /*fromQueue*/ true);
  }

  private clearQueueTimer() {
    if (this.queueTimer !== null) {
      clearTimeout(this.queueTimer);
      this.queueTimer = null;
    }
  }

  private async playOne(id: string, filename: string, fromQueue = false): Promise<void> {
    const gen = ++this.generation;
    this.setState({ playingId: id, playingFile: filename, status: 'loading' });

    if (!this.el) {
      this.el = new Audio();
      this.el.preload = 'auto';
    }
    this.el.pause();
    this.el.src = `${AUDIO_BASE}/${filename}`;

    const onPlay = () => {
      if (gen !== this.generation) return;
      this.setState({ status: 'playing' });
    };
    const onEnd = () => {
      if (gen !== this.generation) return;
      if (fromQueue && this.state.queueId !== null) {
        // Schedule the next item after the gap.
        this.clearQueueTimer();
        this.queueTimer = setTimeout(() => {
          this.queueTimer = null;
          if (gen !== this.generation) return;
          void this.advanceQueue();
        }, this.queueGap);
      } else {
        this.setState({ playingId: null, playingFile: null, status: 'idle' });
      }
    };
    const onError: OnErrorEventHandler = (e) => {
      if (gen !== this.generation) return;
      console.warn(`Audio failed to load: ${filename}`, e);
      // In a queue: skip to the next item rather than abort the whole run.
      if (fromQueue && this.state.queueId !== null) {
        this.clearQueueTimer();
        this.queueTimer = setTimeout(() => {
          this.queueTimer = null;
          if (gen !== this.generation) return;
          void this.advanceQueue();
        }, this.queueGap);
      } else {
        this.setState({ playingId: null, playingFile: null, status: 'error' });
      }
    };

    this.el.onplay = onPlay;
    this.el.onended = onEnd;
    this.el.onerror = onError;

    try {
      await this.el.play();
    } catch (err) {
      if (gen !== this.generation) return;
      console.warn(`Audio play() rejected for ${filename}:`, err);
      if (fromQueue && this.state.queueId !== null) {
        this.clearQueueTimer();
        this.queueTimer = setTimeout(() => {
          this.queueTimer = null;
          if (gen !== this.generation) return;
          void this.advanceQueue();
        }, this.queueGap);
      } else {
        this.setState({ playingId: null, playingFile: null, status: 'error' });
      }
    }
  }
}

export const audioPlayer = new AudioPlayer();
